import { useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonWithLoading } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';

import type {
  FinancialOnboardingState,
  ProductFinanceClient,
} from '#components/product-finance/client';

import {
  actionStyle,
  Field,
  FormError,
  formStyle,
  getFinancialErrorMessage,
  helpStyle,
  inputStyle,
  SourceList,
  StepCard,
} from './onboarding-shared';

export function CycleStep({
  onboarding,
  client,
  onCreated,
  onComplete,
}: Readonly<{
  onboarding: FinancialOnboardingState;
  client: ProductFinanceClient;
  onCreated: () => Promise<unknown>;
  onComplete: () => Promise<void>;
}>) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [anchorId, setAnchorId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (name.trim().length === 0 || anchorId.length === 0) {
      setError(t('Enter a cycle name and choose its fixed-income anchor.'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await client.createBudgetCycle({
        name: name.trim(),
        anchorIncomeTemplateId: anchorId,
        enabled: true,
      });
      setName('');
      setAnchorId('');
      await onCreated();
    } catch (submitError) {
      setError(getFinancialErrorMessage(submitError, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function finish() {
    setIsFinishing(true);
    try {
      await onComplete();
    } finally {
      setIsFinishing(false);
    }
  }

  return (
    <StepCard title={<Trans>Create your budget cycles</Trans>}>
      <p style={helpStyle}>
        <Trans>
          Each cycle starts from a fixed income you choose. Nothing is created
          automatically, so multiple paydays remain explicit and auditable.
        </Trans>
      </p>
      <SourceList
        empty={<Trans>No budget cycles have been added yet.</Trans>}
        items={onboarding.budgetCycles.map(cycle => ({
          id: cycle.id,
          name: cycle.name,
          detail:
            onboarding.fixedIncomeTemplates.find(
              income => income.id === cycle.anchorIncomeTemplateId,
            )?.name ?? cycle.anchorIncomeTemplateId,
        }))}
      />
      <form onSubmit={submit} style={formStyle}>
        <Field label={<Trans>Cycle name</Trans>} htmlFor="cycle-name">
          <input
            id="cycle-name"
            value={name}
            onChange={event => setName(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field
          label={<Trans>Fixed-income anchor</Trans>}
          htmlFor="cycle-anchor"
        >
          <select
            id="cycle-anchor"
            value={anchorId}
            onChange={event => setAnchorId(event.target.value)}
            style={inputStyle}
          >
            <option value="">
              <Trans>Choose an income</Trans>
            </option>
            {onboarding.fixedIncomeTemplates.map(income => (
              <option key={income.id} value={income.id}>
                {income.name}
              </option>
            ))}
          </select>
        </Field>
        <FormError message={error} />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <ButtonWithLoading
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            style={actionStyle}
          >
            <Trans>Add budget cycle</Trans>
          </ButtonWithLoading>
          <ButtonWithLoading
            type="button"
            isLoading={isFinishing}
            isDisabled={
              isSubmitting ||
              isFinishing ||
              onboarding.budgetCycles.length === 0
            }
            onPress={() => void finish()}
            style={actionStyle}
          >
            <Trans>Finish setup</Trans>
          </ButtonWithLoading>
        </View>
      </form>
    </StepCard>
  );
}
